import { AlertCircle } from 'lucide-react'
import template from '@/assets/files/hydra-import-template.xlsx'
import { AxiosError } from 'axios'
import { HTTPValidationError } from '@/generated/types.gen'
import { get } from 'lodash'

// type ErrorKey = 'required_field' | 'invalid_email' | 'email_exists' | 'duplicate_email' | 'invalid_role' | 'quota_exceeded';

const errorMessages: Record<
  string,
  { title: string; description: string; hasTemplate?: boolean }
> = {
  required_field: {
    title: 'Required Field Missing',
    description:
      'One or more required fields are missing. Please check and fill in all required information.',
  },
  invalid_email: {
    title: 'Invalid Email Format',
    description:
      'The e-mail format in the file you uploaded is in the incorrect format. Please help check the instruction in the',
    hasTemplate: true,
  },
  email_exists: {
    title: 'Email Exists',
    description: '',
    // "The selected file contains users already in the system: ([registered_emails]), please update your file and try again.",
  },
  duplicate_email: {
    title: 'Duplicated Emails',
    description:
      'The file you uploaded contains duplicate emails. Please check and try again.',
  },
  invalid_role: {
    title: 'Invalid Role',
    description:
      'One or more specified roles are invalid. Please check the available roles and try again.',
  },
  quota_exceeded: {
    title: 'Exceeded Available Seats',
    description: '',
  },
  invalid_sheet_number: {
    title: 'Missing Sheet Error',
    description:
      'Your imported file was missing team members. Please download the template and consolidate your team members to the second sheet per the template instructions and try again.',
  },
  invalid_header: {
    title: 'Header Format Error',
    description:
      'One or more column headers are either missing or in the incorrect format. Please download our',
    hasTemplate: true,
  },
  unexpected_error: {
    title: 'Unexpected Error',
    description:
      'An unexpected error occurred. Check the format instruction from our',
    hasTemplate: true,
  },
  invalid_characters: {
    title: 'Invalid Name Format',
    description:
      'First and last names must not contain whitespace or special characters.',
  },
}

export default function ErrorMessages({
  errorsInput,
}: {
  errorsInput: AxiosError<HTTPValidationError, any> | null
}) {
  const errors: string[] = get(errorsInput?.response?.data, 'errors', [
    'unexpected_error',
  ])

  if (!errors.length) return null

  return (
    <div className="space-y-4 max-w-2xl">
      <h6 className="text-base font-bold text-destructive">
        {errors.length} {errors.length === 1 ? 'error' : 'errors'} found
      </h6>
      <div className="space-y-3">
        {errors.map((errorKey) => {
          const isEmailExists = errorKey === 'email_exists'
          const registeredEmails = get(
            errorsInput?.response?.data,
            'registered_emails',
            []
          ).map((email: string) => <li key={email}>{email}</li>)

          const isQuotaExceeded = errorKey === 'quota_exceeded'
          const availableSeats = get(
            errorsInput?.response?.data,
            'available_seats',
            0
          )
          const quotaExceededMessage = `Importing these users would result in your organization exceeding it's number of available seats (${availableSeats}). Please update your file or contact your Hydra Score Account Executive to request more seats.`
          const error = errorMessages[errorKey]
          return (
            <div
              key={errorKey}
              className="border-none p-0 flex items-start space-x-2"
            >
              <div className="pt-1">
                <AlertCircle className="text-destructive h-4 w-4" />
              </div>
              <div>
                <p className="font-bold text-sm">{error.title}</p>
                <span className="text-xs">
                  {isEmailExists ? (
                    <div className="flex flex-col gap-3">
                      <p>
                        The selected file contains users already in the system:{' '}
                      </p>
                      <ul className="list-disc pl-4">{registeredEmails}</ul>
                      <p>Please update your file and try again.</p>
                    </div>
                  ) : isQuotaExceeded ? (
                    <p>{quotaExceededMessage}</p>
                  ) : (
                    <>
                      {error.description}
                      {error.hasTemplate && (
                        <>
                          {' '}
                          <a
                            href={template}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline"
                          >
                            template
                          </a>
                          {' and then try again.'}
                        </>
                      )}
                    </>
                  )}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
